import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  useCreateAccountLink,
  type AccountSearchResult,
  type BillingOrphanResult,
} from "../../../renewals/api";
import { AccountSearch } from "../../../renewals/components/account-links/AccountSearch";

import { OrphanSearch } from "./OrphanSearch";

/**
 * Form for adding a sub-account link. Two picker modes:
 *  - Billing-only QB ID: counterparties that bill in QB but have no account
 *    record yet (the orphan-PO case). Preloads top results on open.
 *  - Existing account: any account in the pipeline. 2-char debounced search.
 * Submits a POST /account-links with relationship="subcustomer_of"; the
 * backend auto-invalidates the partners cache for subcustomer_of mutations.
 */
type PickedAccount =
  | { kind: "orphan"; qb_id: number; name: string }
  | { kind: "account"; qb_id: number; name: string };

export function SubAccountForm({
  partner,
  excludeQbs,
  onSuccess,
  onCancel,
}: {
  partner: { qb_id: number; account_id: string; account_name: string };
  excludeQbs: number[];
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"orphan" | "account">("orphan");
  const [orphan, setOrphan] = useState<BillingOrphanResult | null>(null);
  const [account, setAccount] = useState<AccountSearchResult | null>(null);
  const [effective, setEffective] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateAccountLink();
  const picked: PickedAccount | null =
    mode === "orphan"
      ? orphan
        ? { kind: "orphan", qb_id: orphan.qb_id, name: orphan.name }
        : null
      : account
        ? { kind: "account", qb_id: account.qb_id, name: account.name }
        : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!picked) {
      setError("Pick a sub-account.");
      return;
    }
    try {
      await create.mutateAsync({
        source_qb_id: picked.qb_id,
        target_qb_id: partner.qb_id,
        relationship: "subcustomer_of",
        effective_period: effective || null,
        note: note || null,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    }
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-md border border-border bg-muted/30 p-4"
    >
      <div className="flex gap-2">
        <ModeButton
          active={mode === "orphan"}
          onClick={() => {
            setMode("orphan");
            setAccount(null);
          }}
        >
          Billing-only QB ID
        </ModeButton>
        <ModeButton
          active={mode === "account"}
          onClick={() => {
            setMode("account");
            setOrphan(null);
          }}
        >
          Existing account
        </ModeButton>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_1fr]">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="subacct-search"
            className="text-xs uppercase tracking-wider text-muted-foreground"
          >
            {mode === "orphan" ? "Search counterparty name in QB billing" : "Search account name"}
          </label>
          {mode === "orphan" ? (
            <OrphanSearch
              id="subacct-search"
              value={orphan}
              onChange={setOrphan}
              excludeQbs={[...excludeQbs, partner.qb_id]}
            />
          ) : (
            <AccountSearch
              id="subacct-search"
              value={account}
              onChange={setAccount}
              excludeQb={partner.qb_id}
            />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="subacct-effective"
            className="text-xs uppercase tracking-wider text-muted-foreground"
          >
            Effective from
          </label>
          <Input
            id="subacct-effective"
            type="date"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
          />
          <div className="text-[10px] text-muted-foreground">
            billing before this month is excluded from rollups
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="subacct-note"
            className="text-xs uppercase tracking-wider text-muted-foreground"
          >
            Note (optional)
          </label>
          <Input
            id="subacct-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="why this link"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={!picked || create.isPending}>
          {create.isPending ? "Saving…" : "Add sub-account"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={create.isPending}>
          Cancel
        </Button>
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    </form>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-1.5 text-xs font-medium",
        active
          ? "border-border bg-background text-foreground"
          : "border-transparent bg-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
